// JSA Training Info component

import React from "react";
import Select from "react-select";
import { positionSelectStyles } from "../../common/delSelect";
import styles from "./jsaTrainingInfoComponents.module.css";

// Helper function to get field error for Training fields
const getTrainingFieldError = (autosaveErrors, fieldName) => {
  const key = `jsa_training.${fieldName}`;
  return autosaveErrors?.fieldErrors?.[key] || null;
};

// Helper function to get row error for Training employees
const getTrainingRowError = (autosaveErrors, rowId) => {
  return autosaveErrors?.rowErrors?.[rowId] || null;
};

export const JsaTrainingCheckboxes = ({ callbacks }) => {
  // Handling the updated job training completed yes
  const handleUpdateJobTrainingCompletedYes = (e) => {
    callbacks.coreActions.updateJsaTrainingCompletedYes(
      callbacks.jsaData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  // Handling the updated job training completed no
  const handleUpdateJobTrainingCompletedNo = (e) => {
    callbacks.coreActions.updateJsaTrainingCompletedNo(
      callbacks.jsaData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  // Handling the updated hands on job training yes
  const handleUpdateHandsOnJobTrainingYes = (e) => {
    // Setting up an alert to the user that if they unselect this and any data if present
    if (!e.target.checked) {
      if (
        window.confirm(
          "If you unselect this and any data is present in the training information fields, it will be lost. Click Cancel to keep the data or OK to lose the data."
        )
      ) {
        callbacks.coreActions.updateJsaHandsOnJobTrainingYes(
          callbacks.jsaData,
          e.target.checked,
          callbacks.emitPatch
        );
      }
    } else {
      callbacks.coreActions.updateJsaHandsOnJobTrainingYes(
        callbacks.jsaData,
        e.target.checked,
        callbacks.emitPatch
      );
    }
  };

  // Handling the updated hands on job training no
  const handleUpdateHandsOnJobTrainingNo = (e) => {
    // Setting up an alert to the user that if they unselect this and any data if present
    if (e.target.checked) {
      if (
        window.confirm(
          "If you select this and any data is present in the training information fields, it will be lost. Click Cancel to keep the data or OK to lose the data."
        )
      ) {
        callbacks.coreActions.updateJsaHandsOnJobTrainingNo(
          callbacks.jsaData,
          e.target.checked,
          callbacks.emitPatch
        );
      }
    } else {
      callbacks.coreActions.updateJsaHandsOnJobTrainingNo(
        callbacks.jsaData,
        e.target.checked,
        callbacks.emitPatch
      );
    }
  };

  // Handling the updated training information
  return (
    <div className={styles.jsaTrainingCheckboxContainer}>
      <div className={styles.jsaTrainingCheckboxRow}>
        {/* Job Training Completed Yes/No */}
        <label className={styles.jsaTrainingCheckboxLabel}>
          Has training for this job been completed?
        </label>
        <div className={styles.jsaTrainingCheckbox}>
          <input
            type="checkbox"
            id="trainingCompletedYes"
            checked={callbacks.jsaData.jsaTrainingInfo.jobTrainingCompletedYes}
            onChange={handleUpdateJobTrainingCompletedYes}
            disabled={callbacks.jsaMode === "view"}
          />
          <label htmlFor="trainingCompletedYes">Yes</label>
          <input
            type="checkbox"
            id="trainingCompletedNo"
            checked={callbacks.jsaData.jsaTrainingInfo.jobTrainingCompletedNo}
            onChange={handleUpdateJobTrainingCompletedNo}
            disabled={callbacks.jsaMode === "view"}
          />
          <label htmlFor="trainingCompletedNo">No</label>
        </div>
      </div>
      <div className={styles.jsaTrainingCheckboxRow}>
        {/* Hands On Job Training Yes/No */}
        <label className={styles.jsaTrainingCheckboxLabel}>
          Will hands on job training be conducted on site?
        </label>
        <div className={styles.jsaTrainingCheckbox}>
          <input
            type="checkbox"
            id="handsOnTrainingYes"
            checked={callbacks.jsaData.jsaTrainingInfo.handsOnJobTrainingYes}
            onChange={handleUpdateHandsOnJobTrainingYes}
            disabled={callbacks.jsaMode === "view"}
          />
          <label htmlFor="handsOnTrainingYes">Yes</label>
          <input
            type="checkbox"
            id="handsOnTrainingNo"
            checked={callbacks.jsaData.jsaTrainingInfo.handsOnJobTrainingNo}
            onChange={handleUpdateHandsOnJobTrainingNo}
            disabled={callbacks.jsaMode === "view"}
          />
          <label htmlFor="handsOnTrainingNo">No</label>
        </div>
      </div>
    </div>
  );
};

// This field will switch between and EWI employee look up field and a non-EWI employee basic text field
export const JsaTrainerTextField = ({ callbacks }) => {
  // Get autosave errors for trainer fields
  const nonEwiTrainerError = getTrainingFieldError(callbacks.autosaveErrors, "non_ewi_trainer_name");
  const ewiTrainerError = getTrainingFieldError(callbacks.autosaveErrors, "ewi_trainer_id");

  // Handling the updated trainer name (local state only on onChange)
  const handleUpdateNonEwiTrainerName = (e) => {
    callbacks.coreActions.updateJsaNonEwiTrainerName(
      callbacks.jsaData,
      e.target.value,
      null // Don't emit on every keystroke
    );
  };

  // Handle blur non-EWI trainer name - send patch to server
  const handleBlurNonEwiTrainerName = (e) => {
    callbacks.coreActions.updateJsaNonEwiTrainerName(
      callbacks.jsaData,
      e.target.value,
      callbacks.emitPatch // Emit on blur
    );
    try {
      callbacks?.commit?.training?.(
        callbacks.jsaData,
        "non_ewi_trainer_name",
        callbacks.jsaData.jsaTrainingInfo.nonEwiTrainerName
      );
    } catch {}
  };

  // Handling the updated trainer info
  const handleUpdateEwiTrainerInfo = (selected) => {
    callbacks.coreActions.updateJsaEwiTrainerInfo(
      callbacks.jsaData,
      selected,
      callbacks.emitPatch
    );
    // Commit change for autosave (onChange for selects)
    try {
      const payload = selected
        ? {
            ewi_trainer_id: selected?.value ?? null,
            ewi_trainer_name: selected?.label ?? null,
          }
        : { ewi_trainer_id: null, ewi_trainer_name: null };
      callbacks?.commit?.rac?.(callbacks.jsaData, "ewi_trainer_id", payload);
    } catch {}
  };

  // Derive showEwiTrainerInput from data instead of UI state
  const showEwiTrainerInput = callbacks.jsaData.jsaTrainingInfo.ewiTrainerYes;

  return (
    <div className={styles.jsaTrainingTextFieldContainer}>
      {!showEwiTrainerInput ? (
        <div className={styles.jsaTrainingTextFieldRow}>
          <label className={styles.jsaTrainingTextFieldLabel}>
            Trainer Name:
          </label>
          <input
            type="text"
            value={callbacks.jsaData.jsaTrainingInfo.nonEwiTrainerName}
            onChange={handleUpdateNonEwiTrainerName}
            onBlur={handleBlurNonEwiTrainerName}
            className={`${styles.jsaTrainingTextField} ${nonEwiTrainerError ? styles.inputError : ""}`}
            placeholder="Enter Trainer Name"
            disabled={callbacks.jsaMode === "view"}
          />
          {nonEwiTrainerError && <span className={styles.errorText}>{nonEwiTrainerError}</span>}
        </div>
      ) : (
        <div className={styles.jsaTrainingTextFieldRow}>
          <label className={styles.jsaTrainingTextFieldLabel}>
            Trainer Name:
          </label>
          <Select
            value={
              callbacks.jsaData.jsaTrainingInfo.ewiTrainerName
                ? {
                    value: callbacks.jsaData.jsaTrainingInfo.ewiTrainerName,
                    label: callbacks.jsaData.jsaTrainingInfo.ewiTrainerName,
                  }
                : null
            }
            onChange={handleUpdateEwiTrainerInfo}
            options={callbacks.allEmployees.map((emp) => ({
              value: emp.employee_id,
              label: emp.label,
            }))}
            isDisabled={callbacks.jsaMode === "view"}
            styles={{
              ...positionSelectStyles,
              control: (base) => ({
                ...base,
                ...(ewiTrainerError ? { borderColor: "#dc3545", borderWidth: "2px" } : {}),
              }),
            }}
            placeholder="Select Employee"
            isClearable={true}
            isSearchable={true}
          />
          {ewiTrainerError && <span className={styles.errorText}>{ewiTrainerError}</span>}
        </div>
      )}
    </div>
  );
};

export const JsaTrainingEmployeeRow = ({ employee, callbacks }) => {
  // Get row error for this employee
  const rowError = getTrainingRowError(callbacks.autosaveErrors, employee.rowId);
  const employeeIdError = rowError?.employee_id;

  // Handling the updated training employee
  const handleSelectTrainingEmployee = (selected) => {
    const selectedEmployee = {
      employeeId: selected?.value || null,
      employeeName: selected?.label || null,
      rowId: employee.rowId,
    };
    callbacks.coreActions.updateJsaTrainingEmployeeInfo(
      callbacks.jsaData,
      selectedEmployee,
      callbacks.emitPatch
    );
  };

  const handleRemoveTrainingEmployee = () => {
    callbacks.coreActions.updateJsaTrainingRemoveEmployee(
      callbacks.jsaData,
      employee.rowId,
      callbacks.emitPatch
    );
  };

  return (
    <div className={`${styles.employeeCard} ${rowError ? styles.rowError : ""}`} key={employee.rowId}>
      <div className={styles.employeeCardContent}>
        <Select
          value={
            employee.employeeId && employee.employeeName
              ? {
                  value: employee.employeeId,
                  label: employee.employeeName,
                }
              : null
          }
          onChange={handleSelectTrainingEmployee}
          options={callbacks.allEmployees.map((emp) => ({
            value: emp.employee_id,
            label: emp.label,
          }))}
          isDisabled={callbacks.jsaMode === "view"}
          styles={{
            ...positionSelectStyles,
            control: (base) => ({
              ...base,
              ...(employeeIdError ? { borderColor: "#dc3545", borderWidth: "2px" } : {}),
            }),
          }}
          placeholder="Select Employee"
          isClearable={true}
          isSearchable={true}
        />
        {employeeIdError && <span className={styles.errorText}>{employeeIdError}</span>}
      </div>
      <div className={styles.employeeCardActions}>
        <button
          className={styles.jsaTrainingRemoveButton}
          onClick={handleRemoveTrainingEmployee}
          disabled={callbacks.jsaMode === "view"}
        >
          Remove
        </button>
      </div>
    </div>
  );
};

// This will be the section where a user can select all the employees from the employee tab by clicking the auto employee button, or they can add employees manually as well.
export const JsaTrainingEmployeesInfo = ({ callbacks }) => {
  // Handling the updated training auto add employees
  const handleUpdateTrainingAutoAddEmployees = () => {
    callbacks.coreActions.updateJsaTrainingAutoAddEmployees(
      callbacks.jsaData,
      callbacks.delJsaEmployeeList,
      callbacks.emitPatch
    );
  };

  const handleUpdateTrainingAddBlankEmployee = () => {
    callbacks.coreActions.updateJsaTrainingAddBlankEmployee(
      callbacks.jsaData,
      callbacks.emitPatch
    );
  };

  return (
    <div className={styles.jsaTrainingEmployeesInfoContainer}>
      <label className={styles.jsaTrainingEmployeesInfoLabel}>
        Employees in Training:
      </label>

      <div className={styles.jsaTrainingEmployeesCardList}>
        {callbacks.jsaData.jsaTrainingInfo.jsaTrainingEmployeesInfo.map(
          (employee) => (
            <JsaTrainingEmployeeRow
              key={employee.rowId}
              employee={employee}
              callbacks={callbacks}
            />
          )
        )}
      </div>

      <div className={styles.jsaTrainingEmployeesInfoButtonsContainer}>
        <button
          className={styles.jsaTrainingActionButton}
          onClick={handleUpdateTrainingAutoAddEmployees}
          disabled={callbacks.jsaMode === "view"}
        >
          Auto Add Employees
        </button>
        <button
          className={styles.jsaTrainingActionButton}
          onClick={handleUpdateTrainingAddBlankEmployee}
          disabled={callbacks.jsaMode === "view"}
        >
          Add Employee
        </button>
      </div>
    </div>
  );
};

// This field will be the training information field, basically a textarea, but turning it into a component to make it easier to use.
export const JsaTrainingTextArea = ({ callbacks }) => {
  // Get autosave error for training information
  const trainingInfoError = getTrainingFieldError(callbacks.autosaveErrors, "training_information");

  // Handling the updated training information (local state only on onChange)
  const handleUpdateTrainingInformation = (e) => {
    callbacks.coreActions.updateJsaTrainingInformation(
      callbacks.jsaData,
      e.target.value,
      null // Don't emit on every keystroke
    );
  };

  // Handle blur training information - send patch to server
  const handleBlurTrainingInformation = (e) => {
    callbacks.coreActions.updateJsaTrainingInformation(
      callbacks.jsaData,
      e.target.value,
      callbacks.emitPatch // Emit on blur
    );
    try {
      callbacks?.commit?.training?.(
        callbacks.jsaData,
        "training_information",
        callbacks.jsaData.jsaTrainingInfo.trainingInformation
      );
    } catch {}
  };

  return (
    <div className={styles.jsaTrainingTextAreaRow}>
      <label className={styles.jsaTrainingTextAreaLabel}>
        Training Information:
      </label>
      <p className={styles.jsaTrainingTextAreaDescription}>
        (Please provide the type of training below. List out the training topics
        covered, equipment, and skills.)
      </p>
      <textarea
        value={callbacks.jsaData.jsaTrainingInfo.trainingInformation}
        onChange={handleUpdateTrainingInformation}
        onBlur={handleBlurTrainingInformation}
        placeholder="Training Information"
        disabled={callbacks.jsaMode === "view"}
        className={trainingInfoError ? styles.textareaError : ""}
      />
      {trainingInfoError && <span className={styles.errorText}>{trainingInfoError}</span>}
    </div>
  );
};

export const JsaTrainingTextFields = ({ callbacks }) => {
  // Handling the updated ewi trainer yes
  const handleUpdateEwiTrainerYes = (e) => {
    callbacks.coreActions.updateJsaEwiTrainerYes(
      callbacks.jsaData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  // Handling the updated ewi trainer no
  const handleUpdateEwiTrainerNo = (e) => {
    callbacks.coreActions.updateJsaEwiTrainerNo(
      callbacks.jsaData,
      e.target.checked,
      callbacks.emitPatch
    );
  };

  return (
    <div className={styles.jsaTrainingTextFieldsContainer}>
      <div className={styles.jsaTrainingCheckboxRow}>
        <label className={styles.jsaTrainingCheckboxLabel}>
          Is the Trainer an EWI employee?
        </label>
        <div className={styles.jsaTrainingCheckbox}>
          <input
            type="checkbox"
            id="ewiTrainerYes"
            checked={callbacks.jsaData.jsaTrainingInfo.ewiTrainerYes}
            onChange={handleUpdateEwiTrainerYes}
            disabled={callbacks.jsaMode === "view"}
          />
          <label htmlFor="ewiTrainerYes">Yes</label>
          <input
            type="checkbox"
            id="ewiTrainerNo"
            checked={callbacks.jsaData.jsaTrainingInfo.ewiTrainerNo}
            onChange={handleUpdateEwiTrainerNo}
            disabled={callbacks.jsaMode === "view"}
          />
          <label htmlFor="ewiTrainerNo">No</label>
        </div>
      </div>
      <JsaTrainerTextField callbacks={callbacks} />
      <JsaTrainingEmployeesInfo callbacks={callbacks} />
      <JsaTrainingTextArea callbacks={callbacks} />
    </div>
  );
};

export const JsaTrainingInfoTab = ({ callbacks }) => {
  // Derive showTrainingInfo from data instead of UI state - no useEffect needed
  const showTrainingInfo =
    callbacks.jsaData.jsaTrainingInfo.handsOnJobTrainingYes;

  return (
    <div className={styles.jsaTrainingInfoContainer}>
      <JsaTrainingCheckboxes callbacks={callbacks} />

      {/* Render the fields always, but toggle the visible/hidden class */}
      <div
        className={`${styles.jsaTrainingTextFieldsWrapper} ${
          showTrainingInfo
            ? styles.jsaTrainingFieldsVisible
            : styles.jsaTrainingFieldsHidden
        }`}
      >
        <JsaTrainingTextFields callbacks={callbacks} />
      </div>
    </div>
  );
};
